<script setup lang="ts">

import TrialStatusContainer from "@/trial/components/trial-status/TrialStatusContainer.vue";
import Icon from "@/core/components/icon/Icon.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {ref} from "vue";
import {subscriptionPlanApi} from "@/container";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {storeToRefs} from "pinia";
import {useExtensionStore} from "@/stores/extension.store";
import GroupingAdvantageButton from "@/advantage/components/GroupingAdvantageButton.vue";
import useHealthcenterAdvantages from "@/advantage/composables/useHealthcenterAdvantages";
import {Skeleton} from "@/core/components/ui/skeleton";
import MarkdownView from "@/core/components/markdown/Markdown.vue";

const loading = ref(false);
const store = useSubscriptionPlanStore();
const extensionStore = useExtensionStore();
const {globalPlan} = storeToRefs(store);
const {data, isLoading} = useHealthcenterAdvantages();

const emits = defineEmits<{
    (e: 'trialLaunched'): void;
}>();

/**
 * Launch the trial period for the global plan.
 */
const launchTrial = async () => {
    if (!globalPlan.value) {
        return;
    }

    try {
        loading.value = true;
        await subscriptionPlanApi().launchTrial(globalPlan.value.id);
        emits('trialLaunched');
        await Promise.all([
            store.refreshSubscriptionPlans(),
            extensionStore.refreshExtensions()
        ]);
    } finally {
        loading.value = false;
    }
};

</script>

<template>
  <TrialStatusContainer>
    <template #headline>
      <div class="space-y-2">
        <div class="flex items-center gap-2">
          <Icon name="mdi-crown" color="primary" :size="30" />
          <p>Activez votre essai gratuit pour utiliser les modules et débloquer la popup au comptoir</p>
        </div>
        <div class="flex text-primary justify-between gap-4 flex-wrap pl-1 relative">
          <router-link :to="{name: 'subscription-plans'}">
            <p class="underline">
              Découvrir les avantages Premium<span class="no-underline inline-block">&nbsp;🚀</span>
            </p>
          </router-link>
          <div>
            <BaseButton
              primary density="compact" :loading="loading"
              class="relative bottom-2"
              @click="launchTrial"
            >
              <span class="tracking-normal">Commencer mon essai gratuit de 2 mois</span>
            </BaseButton>
            <p class="text-xs text-end">
              Sans carte bancaire, sans engagement
            </p>
          </div>
        </div>
        <Skeleton v-if="isLoading" class="w-48 h-4" />
        <div v-else class="flex flex-col items-start">
          <div v-for="advantage in data" :key="advantage.id" class="flex items-baseline">
            <p>🎁</p>
            <MarkdownView
              :content="advantage.sales_pitch" class="text-primary font-bold text-lg"
              :inherit-styles="true"
            />
          </div>
          <GroupingAdvantageButton :healthcenter-has-advantages="!!data?.length" />
        </div>
      </div>
    </template>
  </TrialStatusContainer>
</template>

<style scoped>

</style>
